const SYSTEM_SYNC_KEYS = new Set([
  'id',
  'word_id',
  'dictionary_id',
  'language_code',
  'word',
  'short_word',
  'part_of_speech_id',
  'part_of_speech_name',
  'translations',
  'translation',
  'phonetics_map',
  'phonetics',
  'hint',
  'updated_at',
  'updatedTime',
  'revision',
  'sync_version',
]);

export function cloneValue<T>(value: T): T {
  if (value === null || value === undefined || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => cloneValue(item)) as unknown as T;
  }
  const copy: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    copy[k] = cloneValue(v);
  }
  return copy as T;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || a === undefined || b === undefined) {
    return a === b;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (isPlainObject(a) || isPlainObject(b)) {
    if (!isPlainObject(a) || !isPlainObject(b)) return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }
  return false;
}

/**
 * Recursive structural 3-way merge matching TaskManager's `mergeLocalOverRemote(base, remote, local, path)`.
 * - System metadata keys (`updated_at`, `sync_version`, immutable word descriptors) always resolve from `remote`.
 * - For each key on an object:
 *   - If `local[key] == base[key]` (unchanged locally) -> take `remote[key]` (preserves changes made on other devices / Telegram Bot).
 *   - Else if `remote[key] == base[key]` or `remote[key] == local[key]` -> take `local[key]`.
 *   - Else -> recursively 3-way merge `mergeLocalOverRemote(base[key], remote[key], local[key], path + '/' + key)`.
 */
export function mergeLocalOverRemote<T = Record<string, any>>(
  base: unknown,
  remote: unknown,
  local: unknown,
  path = ''
): T {
  if (isPlainObject(base) || isPlainObject(remote) || isPlainObject(local)) {
    const baseObj = isPlainObject(base) ? base : {};
    const remoteObj = isPlainObject(remote) ? remote : {};
    const localObj = isPlainObject(local) ? local : {};

    const keys = new Set([
      ...Object.keys(baseObj),
      ...Object.keys(remoteObj),
      ...Object.keys(localObj),
    ]);

    const merged: Record<string, unknown> = {};
    for (const key of keys) {
      if (!path && SYSTEM_SYNC_KEYS.has(key)) {
        if (key in remoteObj) {
          merged[key] = cloneValue(remoteObj[key]);
        } else if (key in localObj) {
          merged[key] = cloneValue(localObj[key]);
        } else if (key in baseObj) {
          merged[key] = cloneValue(baseObj[key]);
        }
        continue;
      }

      const bVal = baseObj[key];
      const rVal = remoteObj[key];
      const lVal = localObj[key];
      const subPath = `${path}/${key}`;

      if (!(key in localObj) && key in baseObj) {
        // Deleted locally
        continue;
      }

      if (deepEqual(lVal, bVal)) {
        if (key in remoteObj) {
          merged[key] = cloneValue(rVal);
        }
      } else if (deepEqual(rVal, bVal) || deepEqual(rVal, lVal)) {
        if (key in localObj) {
          merged[key] = cloneValue(lVal);
        }
      } else {
        merged[key] = mergeLocalOverRemote(bVal, rVal, lVal, subPath);
      }
    }
    return merged as T;
  }

  if (Array.isArray(base) || Array.isArray(remote) || Array.isArray(local)) {
    return cloneValue(deepEqual(local, base) ? remote : local) as T;
  }

  return cloneValue(deepEqual(local, base) ? remote : local) as T;
}
